import { yupResolver } from "@hookform/resolvers/yup";
import { cn } from "@omni/utils/styles";
import { useEffect } from "react";
import { FormProvider } from "react-hook-form";

import { FormGroup } from "@/components/Form/Base/formGroup.component";
import { Label } from "@/components/Form/Base/label.component";
import { PasswordField } from "@/components/Form/RHF/FormFields/PasswordField/passwordField.component";
import { TextField } from "@/components/Form/RHF/FormFields/TextField/Textfield.component";
import { useOmniForm } from "@/components/Form/RHF/Utils/useOmniForm";
import { Col } from "@/components/General/Grid/col.component";
import { Container } from "@/components/General/Grid/container.component";
import { Row } from "@/components/General/Grid/row.component";
import { Messages } from "@/components/General/Messages/messages.container";
import { clearMessageById } from "@/redux/modules/value/messages/messages.utils";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { FormInfoProvider } from "@/utils/forms/formInfo.provider";

import {
    EditEmailFormValidationSchema,
    EditEmailFormValues
} from "./editEmail.schema";

type EditEmailFormProps = {
    currentEmail: string;
    onSubmit: (language: string, email: string) => void;
};

const FORM_ID = "profileEditEmailForm";

const EditEmailForm = ({ currentEmail, onSubmit }: EditEmailFormProps) => {
    const { t } = useGranite();

    const methods = useOmniForm({
        mode: "onBlur",
        reValidateMode: "onBlur",
        defaultValues: {
            newEmail: "",
            password: ""
        },
        resolver: yupResolver(EditEmailFormValidationSchema)
    });

    useEffect(() => {
        clearMessageById("edit-email-failure");
    }, []);

    const onFormSubmit = ({ newEmail, password }: EditEmailFormValues) => {
        clearMessageById("edit-email-failure");
        onSubmit?.(newEmail, password);
    };

    return (
        <div>
            <Messages messageid={"edit-email-failure"} />
            <FormGroup>
                <Label>{t("general.form.currentEmail.label")}</Label>
                <div>{currentEmail}</div>
            </FormGroup>
            <FormInfoProvider
                formId={FORM_ID}
                withNeverbounce={true}
            >
                {({ initialized }) => (
                    <FormProvider {...methods}>
                        <form
                            id={FORM_ID}
                            onSubmit={methods.handleSubmit(onFormSubmit)}
                        >
                            <Container className={cn("tw:px-0")}>
                                <Row>
                                    <Col>
                                        <TextField<EditEmailFormValues>
                                            name="newEmail"
                                            label={t(
                                                "general.form.newEmail.label"
                                            )}
                                            required={true}
                                            autoComplete={"new-email"}
                                            disabled={!initialized}
                                        />
                                        <PasswordField<EditEmailFormValues>
                                            name="password"
                                            label={t(
                                                "general.form.password.label"
                                            )}
                                            required={true}
                                            autoComplete={"new-password"}
                                            disabled={!initialized}
                                        />
                                    </Col>
                                </Row>
                            </Container>
                        </form>
                    </FormProvider>
                )}
            </FormInfoProvider>
        </div>
    );
};

export default EditEmailForm;
